import { CalendarBlank } from "@phosphor-icons/react";
import { STATUS_LABEL } from "../lib/labels";

const STATUS_CONFIG = {
  todo: { label: STATUS_LABEL.todo, color: "#71717A" },
  doing: { label: STATUS_LABEL.doing, color: "#007AFF" },
  with_editor: { label: STATUS_LABEL.with_editor, color: "#A855F7" },
  to_be_checked: { label: STATUS_LABEL.to_be_checked, color: "#F59E0B" },
  checked: { label: STATUS_LABEL.checked, color: "#10B981" },
  done: { label: STATUS_LABEL.done, color: "#22C55E" },
};

const STATUS_ORDER = ["todo", "doing", "to_be_checked", "with_editor", "checked", "done"];

function allowedTransitions(role, status, task, user) {
  if (role === "admin") {
    return STATUS_ORDER.filter((s) => s !== status && s !== "with_editor");
  }
  const out = new Set();
  const uid = user.id;
  const isTaskAssigner = task.assigner_id === uid;
  const isTaskProducer = task.worker_id === uid;
  const isTaskChecker = task.checker_id === uid;

  // Ready-to-Publish: task's specific assigner / producer / copy editor
  // move it from With Video Editor to Ready to be Published.
  if (status === "with_editor" && (isTaskAssigner || isTaskProducer || isTaskChecker)) {
    out.add("checked");
  }

  // Producer transitions (worker + hybrid assigner)
  if (role === "worker" || role === "assigner") {
    if (!task.worker_id || task.worker_id === uid) {
      const map = { todo: ["doing"], doing: ["to_be_checked"], checked: ["done"] };
      (map[status] || []).forEach((s) => out.add(s));
    }
  }
  // Video editor gets NO status transitions in the new workflow —
  // completion is signalled by the "Video Edit Done" marker.
  // Copy editor transitions (checker + hybrid assigner)
  if (role === "checker" || role === "assigner") {
    if (!task.checker_id || task.checker_id === uid) {
      if (status === "to_be_checked") {
        out.add("with_editor");
        out.add("doing");
      }
    }
  }
  // Publisher — self-claim on Ready to be Published, single transition to Published
  if (role === "publisher") {
    if (!task.publisher_id || task.publisher_id === uid) {
      if (status === "checked") out.add("done");
    }
  }
  return Array.from(out);
}

export function KanbanBoard({ tasks, members, currentUser, onStatusChange, onOpenTask, onGfxGiven, onClaimChecker, onClaimEditor, onStartVideoEdit, onVideoEditDone, doneTotal = 0, onLoadMoreDone, loadingMoreDone = false }) {
  const memberMap = Object.fromEntries(members.map((m) => [m.id, m]));

  return (
    <div className="scrollable-x" data-testid="kanban-board">
      <div className="flex gap-3 xl:gap-2 pb-4">
        {STATUS_ORDER.map((status) => {
          const cfg = STATUS_CONFIG[status];
          // Every column shows the most recently added task at the top so the
          // team always knows what's fresh without scrolling. For the "Done"
          // column, use updated_at so the most recently *published* task
          // stays at the top (feels the same as "just added" for that stage).
          const orderKey = status === "done" ? "updated_at" : "created_at";
          const colTasks = [...tasks.filter((t) => t.status === status)].sort((a, b) => {
            const at = a[orderKey] || a.created_at || "";
            const bt = b[orderKey] || b.created_at || "";
            return bt.localeCompare(at);
          });
          return (
            <div key={status} className="kanban-col" data-testid={`kanban-column-${status}`}>
              <div className="px-4 py-3 border-b border-[color:var(--border)] flex items-center justify-between sticky top-0 bg-[color:var(--surface)]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
                  <span className="label-mono" style={{ color: cfg.color }}>{cfg.label}</span>
                </div>
                <span className="text-xs font-mono text-[color:var(--text-tertiary)]">
                  {status === "done" && doneTotal > colTasks.length
                    ? `${colTasks.length}/${doneTotal}`
                    : colTasks.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {colTasks.length === 0 && (
                  <div className="text-xs text-[color:var(--text-tertiary)] text-center py-6">No tasks</div>
                )}
                {colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    members={memberMap}
                    currentUser={currentUser}
                    onClick={() => onOpenTask(task)}
                    onMove={(newStatus) => onStatusChange(task.id, newStatus)}
                    onGfxGiven={() => onGfxGiven?.(task.id)}
                    onClaimChecker={() => onClaimChecker?.(task.id)}
                    onClaimEditor={() => onClaimEditor?.(task.id)}
                    onStartVideoEdit={() => onStartVideoEdit?.(task.id)}
                    onVideoEditDone={() => onVideoEditDone?.(task.id)}
                  />
                ))}
                {status === "done" && doneTotal > colTasks.length && (
                  <button
                    data-testid="load-more-published-btn"
                    onClick={onLoadMoreDone}
                    disabled={loadingMoreDone}
                    className="w-full text-xs font-heading font-bold uppercase tracking-wider py-2 rounded border border-dashed border-[#22C55E66] text-[#22C55E] hover:bg-[#22C55E11] transition disabled:opacity-50"
                  >
                    {loadingMoreDone ? "Loading…" : `Load 50 more (${doneTotal - colTasks.length} remaining)`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskCard({ task, members, currentUser, onClick, onMove, onGfxGiven, onClaimChecker, onClaimEditor, onStartVideoEdit, onVideoEditDone }) {
  const worker = task.worker_id ? members[task.worker_id] : null;
  const checker = task.checker_id ? members[task.checker_id] : null;
  const videoEditor = task.video_editor_id ? members[task.video_editor_id] : null;
  const graphicDesigner = task.graphic_designer_id ? members[task.graphic_designer_id] : null;
  const publisher = task.publisher_id ? members[task.publisher_id] : null;
  const transitions = allowedTransitions(currentUser.role, task.status, task, currentUser);

  // GFX Given button: only for graphic_designer role, on tasks with_editor, not yet given
  const canMarkGfx = (currentUser.role === "graphic_designer" || currentUser.role === "admin")
    && task.status === "with_editor"
    && !task.gfx_given_at;

  // Copy editor pre-claim: checker or assigner can grab a task while it's upstream
  const canPreClaimChecker = (currentUser.role === "checker" || currentUser.role === "assigner" || currentUser.role === "admin")
    && (task.status === "doing" || task.status === "with_editor")
    && !task.checker_id;

  // Video editor self-claim (ownership only, no timer): only shown when the
  // task is claimed by someone else and this VE wants to take over. Untaken
  // tasks are now claimed by clicking "I've started" (which also records the
  // start time), so we hide the plain claim button when nobody has it yet.
  const canClaimEditor = (currentUser.role === "video_editor" || currentUser.role === "admin")
    && task.status === "with_editor"
    && !!task.video_editor_id
    && task.video_editor_id !== currentUser.id;

  // "I've started" — records video_edit_started_at + self-claims if unset.
  // Only shown while the task is With Video Editor, the timer hasn't been
  // started yet, and either nobody has claimed it or the current user is the
  // assigned VE.
  const canStartVideoEdit = (currentUser.role === "video_editor" || currentUser.role === "admin")
    && task.status === "with_editor"
    && !task.video_edit_started_at
    && (!task.video_editor_id || task.video_editor_id === currentUser.id);

  // Video Edit Done marker (analog to GFX Given) — VE signals completion without moving status
  const canMarkVideoEditDone = (currentUser.role === "video_editor" || currentUser.role === "admin")
    && task.status === "with_editor"
    && !task.video_edit_done_at
    && (!task.video_editor_id || task.video_editor_id === currentUser.id);

  const due = task.due_date ? new Date(task.due_date) : null;
  const isOverdue = due && due < new Date() && task.status !== "done";

  return (
    <div className="task-card" onClick={onClick} data-testid={`task-card-${task.id}`}>
      <h4 className="font-heading font-semibold text-sm leading-snug mb-2">{task.title}</h4>
      {task.description && (
        <p className="text-xs text-[color:var(--text-secondary)] line-clamp-2 mb-3">{task.description}</p>
      )}
      <div className="space-y-1.5 mb-2" data-testid={`task-people-${task.id}`}>
        <PersonRow roleLabel="Producer" tag="PRO" name={worker?.name} color="#4ADE80" />
        <PersonRow roleLabel="Video Editor" tag="VE" name={videoEditor?.name} color="#C084FC" />
        <PersonRow roleLabel="Graphic Designer" tag="GD" name={graphicDesigner?.name} color="#38BDF8" />
        <PersonRow roleLabel="Copy Editor" tag="CE" name={checker?.name} color="#FBBF24" />
        <PersonRow roleLabel="Publisher" tag="PUB" name={publisher?.name} color="#F472B6" />
      </div>
      {task.gfx_given_at && (
        <p className="text-[10px] font-mono uppercase tracking-wider text-[#38BDF8] mb-1" data-testid={`gfx-given-badge-${task.id}`}>
          ✓ GFX given
        </p>
      )}
      {task.video_edit_started_at && !task.video_edit_done_at && (
        <p className="text-[10px] font-mono uppercase tracking-wider text-[#C084FC] mb-1 opacity-80" data-testid={`video-edit-started-badge-${task.id}`}>
          ● Editing in progress
        </p>
      )}
      {task.video_edit_done_at && (
        <p className="text-[10px] font-mono uppercase tracking-wider text-[#C084FC] mb-2" data-testid={`video-edit-done-badge-${task.id}`}>
          ✓ Video edit done
        </p>
      )}
      {due && (
        <div className="flex items-center gap-2 text-xs mt-2">
          <span className={`inline-flex items-center gap-1 ${isOverdue ? "text-red-400" : "text-[color:var(--text-tertiary)]"}`}>
            <CalendarBlank size={12} /> {due.toLocaleDateString()}
          </span>
        </div>
      )}
      {(transitions.length > 0 || canMarkGfx || canPreClaimChecker || canClaimEditor || canStartVideoEdit || canMarkVideoEditDone) && (
        <div className="mt-3 pt-3 border-t border-[color:var(--border)] flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
          {canMarkVideoEditDone && (
            <button
              data-testid={`video-edit-done-${task.id}`}
              onClick={onVideoEditDone}
              className="text-[10px] font-heading font-black uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{ color: "#C084FC", borderColor: "#C084FC", background: "rgba(192, 132, 252, 0.10)" }}
            >
              Video Edit Done
            </button>
          )}
          {canStartVideoEdit && (
            <button
              data-testid={`start-video-edit-${task.id}`}
              onClick={onStartVideoEdit}
              title="Start the editor timer + claim this task"
              className="text-[10px] font-heading font-black uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{ color: "#C084FC", borderColor: "#C084FC", background: "rgba(192, 132, 252, 0.10)" }}
            >
              I've started
            </button>
          )}
          {canClaimEditor && (
            <button
              data-testid={`claim-editor-${task.id}`}
              onClick={onClaimEditor}
              title="Take over as the video editor for this task"
              className="text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{ color: "#C084FC", borderColor: "#C084FC55" }}
            >
              Take over VE
            </button>
          )}
          {canMarkGfx && (
            <button
              data-testid={`mark-gfx-given-${task.id}`}
              onClick={onGfxGiven}
              className="text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{ color: "#38BDF8", borderColor: "#38BDF855" }}
            >
              GFX Given
            </button>
          )}
          {canPreClaimChecker && (
            <button
              data-testid={`claim-checker-${task.id}`}
              onClick={onClaimChecker}
              title="Claim this task as its Copy Editor before it lands in your column"
              className="text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{ color: "#FBBF24", borderColor: "#FBBF2455" }}
            >
              Claim as Copy Editor
            </button>
          )}
          {transitions.map((s) => (
            <button
              key={s}
              data-testid={`move-${task.id}-to-${s}`}
              onClick={() => onMove(s)}
              className="text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded border transition"
              style={{
                color: STATUS_CONFIG[s].color,
                borderColor: STATUS_CONFIG[s].color + "55",
              }}
            >
              → {STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonRow({ roleLabel, tag, name, color }) {
  const display = name ? name.split(" ")[0] : "—";
  const assigned = Boolean(name);
  return (
    <div
      className="flex items-center gap-2 text-xs"
      title={`${roleLabel}: ${name || "Not yet assigned"}`}
      data-testid={`person-row-${tag.toLowerCase()}`}
    >
      <span
        className="font-mono font-bold text-[10px] tracking-wider w-7 shrink-0"
        style={{ color }}
      >
        {tag}
      </span>
      <span
        className={assigned ? "text-[color:var(--text-secondary)] truncate" : "text-[color:var(--text-tertiary)] italic"}
      >
        {display}
      </span>
    </div>
  );
}

